"use client"

import {
  Pencil,
  Trash2,
  Calculator,
  Languages,
  Sprout,
  Globe2,
  Microscope,
  MessageCircle,
  Computer,
  Dumbbell,
} from "lucide-react"
import type { Subject } from "../../../../lib/subject-data"

interface SubjectTableRowProps {
  subject: Subject
  onEdit: (subject: Subject) => void
  onDelete: (id: string) => void
}

const iconMap = {
  calculator: Calculator,
  languages: Languages,
  sprout: Sprout,
  "globe-2": Globe2,
  microscope: Microscope,
  "message-circle": MessageCircle,
  computer: Computer,
  dumbbell: Dumbbell,
}

const colorMap = {
  orange: "bg-orange-100 text-orange-600",
  purple: "bg-purple-100 text-purple-600",
  green: "bg-green-100 text-green-600",
  pink: "bg-pink-100 text-pink-600",
  blue: "bg-blue-100 text-blue-600",
  amber: "bg-amber-100 text-amber-600",
  indigo: "bg-indigo-100 text-indigo-600",
  red: "bg-red-100 text-red-600",
}

export function SubjectTableRow({ subject, onEdit, onDelete }: SubjectTableRowProps) {
  const Icon = iconMap[subject.icon as keyof typeof iconMap] || Calculator
  const colorClass = colorMap[subject.color as keyof typeof colorMap] || colorMap.orange

  return (
    <tr className="hover:bg-muted/50 transition-colors">
      <td className="p-5">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${colorClass}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="font-bold text-foreground">{subject.name}</p>
            <p className="text-xs text-muted-foreground">{subject.description.substring(0, 30)}...</p>
          </div>
        </div>
      </td>
      <td className="p-5 text-sm text-muted-foreground font-mono">{subject.code}</td>
      <td className="p-5">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {subject.enrolled} Students
        </span>
      </td>
      <td className="p-5 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <img
            alt={subject.teacher}
            className="w-6 h-6 rounded-full object-cover"
            src={subject.teacherAvatar || "/placeholder.svg"}
          />
          <span>{subject.teacher}</span>
        </div>
      </td>
      <td className="p-5 text-right">
        <div className="flex justify-end gap-2">
          <button
            onClick={() => onEdit(subject)}
            className="p-2 text-primary hover:bg-teal-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(subject.id)}
            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  )
}
